import { lazy as reactLazy, type ComponentType } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { FeatureSync }  from '@/components/feature/FeatureSync'
import { AuthLayout }   from '@/layouts/AuthLayout'
import { AppLayout }    from '@/layouts/AppLayout'
import { RequireAuth }  from '@/auth/RequireAuth'
import { useAuth }      from '@/auth/useAuth'
import { PATHS }        from '@/lib/routes'

// ── Lazy view imports — enable route-level code splitting ──────────────────────
const lazy = <T extends ComponentType>(load: () => Promise<{ [k: string]: T }>, name: string) =>
  reactLazy(() => load().then(m => ({ default: m[name] as T })))

// Auth views
const LoginView            = lazy(() => import('@/features/auth/LoginView'),               'LoginView')
const ResetPasswordView    = lazy(() => import('@/features/auth/ResetPasswordView'),       'ResetPasswordView')
const RegisterView         = lazy(() => import('@/features/register/RegisterView'),         'RegisterView')
const RegisterPersonalView = lazy(() => import('@/features/register/RegisterPersonalView'), 'RegisterPersonalView')
const RegisterBusinessView = lazy(() => import('@/features/register/RegisterBusinessView'), 'RegisterBusinessView')
const LegalPrivacyView     = lazy(() => import('@/features/register/LegalPrivacyView'),    'LegalPrivacyView')
const LegalTermsView       = lazy(() => import('@/features/register/LegalTermsView'),      'LegalTermsView')

// App views
const DashboardView     = lazy(() => import('@/features/dashboard/DashboardView'),           'DashboardView')
const TransactionsView  = lazy(() => import('@/features/transactions/TransactionsView'),     'TransactionsView')
const ResumenView       = lazy(() => import('@/features/transactions/ResumenView'),          'ResumenView')
const MovementReceiptView = lazy(() => import('@/features/transactions/MovementReceiptView'), 'MovementReceiptView')
const SendMoneyView     = lazy(() => import('@/features/send-money/SendMoneyView'),          'SendMoneyView')
const BalancesView      = lazy(() => import('@/features/balances/BalancesView'),             'BalancesView')
const NotificationsView = lazy(() => import('@/features/notifications/NotificationsView'),   'NotificationsView')
const ProfileView       = lazy(() => import('@/features/profile/ProfileView'),               'ProfileView')
const AdminProfilesView = lazy(() => import('@/features/profiles/AdminProfilesView'),        'AdminProfilesView')
const SettingsView        = lazy(() => import('@/features/settings/SettingsView'),        'SettingsView')
const SecurityView        = lazy(() => import('@/features/settings/SecurityView'),        'SecurityView')
const DeleteAccountView   = lazy(() => import('@/features/settings/DeleteAccountView'),   'DeleteAccountView')
const HelpView          = lazy(() => import('@/features/more-options/HelpView'),             'HelpView')
const LegalView         = lazy(() => import('@/features/more-options/LegalView'),            'LegalView')
const MoreOptionsView   = lazy(() => import('@/features/more-options/MoreOptionsView'),      'MoreOptionsView')
const MyAccountView         = lazy(() => import('@/features/more-options/MyAccountView'),             'MyAccountView')
const EditAliasView         = lazy(() => import('@/features/profile/EditAliasView'),               'EditAliasView')
// These two take a `kind` prop — the generic zero-prop `lazy` helper above
// can't type that through, so they use `reactLazy` directly.
const EditContactVerifyView = reactLazy(() => import('@/features/profile/EditContactVerifyView').then(m => ({ default: m.EditContactVerifyView })))
const EditContactOtpView    = reactLazy(() => import('@/features/profile/EditContactOtpView').then(m => ({ default: m.EditContactOtpView })))
const EditEmailView         = lazy(() => import('@/features/profile/EditEmailView'),         'EditEmailView')
const EditPhoneView         = lazy(() => import('@/features/profile/EditPhoneView'),         'EditPhoneView')
const EditAddressView       = lazy(() => import('@/features/profile/EditAddressView'),       'EditAddressView')

// Root layout: mounts <FeatureSync /> inside React Router's context
// (it depends on useLocation and cannot live in main.tsx).
function RootLayout() {
  return (
    <>
      <FeatureSync />
      <Outlet />
    </>
  )
}

// Redirects / to dashboard when authenticated, login otherwise.
function RootRedirect() {
  const { status } = useAuth()
  if (status === 'loading') return null
  return <Navigate to={status === 'authenticated' ? PATHS.dashboard : PATHS.login} replace />
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <RootRedirect /> },

      // Auth — no chrome
      {
        element: <AuthLayout />,
        children: [
          { path: '/login',                                          element: <LoginView /> },
          { path: '/forgot-password',                                element: <Navigate to={PATHS.login} replace /> },
          { path: '/resetPassword/:token',                           element: <ResetPasswordView /> },
          { path: '/onboarding',                                     element: <RegisterView /> },
          { path: '/onboarding/personal-account',                    element: <RegisterPersonalView /> },
          { path: '/onboarding/professional-account',                element: <RegisterBusinessView /> },
          { path: '/onboarding/legal/privacy-policy',                element: <LegalPrivacyView /> },
          { path: '/onboarding/legal/terms-and-conditions',          element: <LegalTermsView /> },
          // Aliases (same element as their /onboarding/* counterparts)
          { path: '/register',                                       element: <RegisterView /> },
          { path: '/register/personal',                              element: <RegisterPersonalView /> },
          { path: '/register/business',                              element: <RegisterBusinessView /> },
        ],
      },

      // App — protected + chrome (Sidebar + Header + Breadcrumb + BottomNav)
      {
        element: <RequireAuth><AppLayout /></RequireAuth>,
        children: [
          { path: '/dashboard',             element: <DashboardView /> },
          { path: '/transactions',          element: <TransactionsView /> },
          { path: '/transactions/resumen',  element: <ResumenView /> },
          { path: '/transactions/movement/:id', element: <MovementReceiptView /> },
          { path: '/send-money',            element: <SendMoneyView /> },
          { path: '/balances',              element: <BalancesView /> },
          { path: '/notifications',         element: <NotificationsView /> },
          { path: '/profile',               element: <ProfileView /> },
          { path: '/profiles',              element: <AdminProfilesView /> },
          { path: '/settings',                element: <SettingsView /> },
          { path: '/settings/security',     element: <SecurityView /> },
          { path: '/settings/delete-account', element: <DeleteAccountView /> },
          { path: '/help',                  element: <HelpView /> },
          { path: '/legal',                 element: <LegalView /> },
          { path: '/more-options',          element: <MoreOptionsView /> },
          { path: '/my-account',            element: <MyAccountView /> },
          { path: '/profile/edit-alias',   element: <EditAliasView /> },
          { path: PATHS.editEmailVerify,   element: <EditContactVerifyView kind="email" /> },
          { path: PATHS.editEmailOtp,      element: <EditContactOtpView kind="email" /> },
          { path: PATHS.editEmail,         element: <EditEmailView /> },
          { path: PATHS.editPhoneVerify,   element: <EditContactVerifyView kind="phone" /> },
          { path: PATHS.editPhoneOtp,      element: <EditContactOtpView kind="phone" /> },
          { path: PATHS.editPhone,         element: <EditPhoneView /> },
          { path: PATHS.editAddress,       element: <EditAddressView /> },
        ],
      },

      { path: '*', element: <Navigate to={PATHS.login} replace /> },
    ],
  },
])
